import { ReactNode, createContext, useContext, useState, useCallback, useEffect } from 'react';
import { WebSocketContext } from './websocket-context/websocket-provider';
import { VolSurfaceToken } from '../model/currency_type.enum';
import usePersistentState from '../providers/use-persistent-state';
import { WidgetContext } from '../components/v2/widget-renderer/widget-provider';

export enum WindowEnum {
  MIN10 = '10m',
  H1 = '1H',
  D1 = '1D',
  D2 = '2D',
  D3 = '3D',
  D4 = '4D',
  D5 = '5D',
  D6 = '6D',
  W1 = '1W',
  W2 = '2W',
  M1 = '1M',
  M2 = '2M',
  M3 = '3M',
  M6 = '6M',
  M9 = '9M',
  Y1 = '1Y',
}

export enum MetricEnum {
  ATMF = 'ATMF',

  P5 = '5P',
  P10 = '10P',
  P15 = '15P',
  P20 = '20P',
  P25 = '25P',
  P30 = '30P',
  P35 = '35P',
  P40 = '40P',

  C5 = '5C',
  C10 = '10C',
  C15 = '15C',
  C20 = '20C',
  C25 = '25C',
  C30 = '30C',
  C35 = '35C',
  C40 = '40C',
}

const KEY = 'VOLATILITY_CONES';
interface VolConeRequestParams {
  underlying: VolSurfaceToken;
  metric: MetricEnum;
  window: WindowEnum;
  format: string;
  as_at_time: string;
}

interface VolConeResponse {
  current: Record<string, number>;
  low: Record<string, number>;
  p25: Record<string, number>;
  median: Record<string, number>;
  p75: Record<string, number>;
  high: Record<string, number>;
}

export interface VolConeItem {
  tenor: string;
  current: number;
  low: number;
  p25: number;
  median: number;
  p75: number;
  high: number;
}

export type VolConeData = VolConeItem[];

interface IVolatilityConesContext {
  data: VolConeData | null;
  isLoading: boolean;
  fetchVolCone: (params: VolConeRequestParams) => void;
  lastTimestamp: string;
  setLastTimestamp: React.Dispatch<React.SetStateAction<string>>;
  token: VolSurfaceToken;
  setToken: (t: VolSurfaceToken) => void;
  selectedPeriod: WindowEnum;
  setSelectedPeriod: (w: WindowEnum) => void;
  selectedTenor: MetricEnum;
  setSelectedTenor: (m: MetricEnum) => void;
  isLive: boolean;
  resetToLive: () => void;
  setHistoricalDate: (timestamp: string) => void;
}

export const VolatilityConesContext = createContext<IVolatilityConesContext>(
  {} as IVolatilityConesContext,
);

interface Props {
  children: ReactNode;
  providerKey: string;
}

function isVolConeResponse(obj: unknown): obj is VolConeResponse {
  if (typeof obj !== 'object' || obj === null) return false;
  const keys = ['current', 'low', 'p25', 'median', 'p75', 'high'];
  return keys.every(
    (key) =>
      key in obj &&
      typeof (obj as any)[key] === 'object' &&
      (obj as any)[key] !== null &&
      !Array.isArray((obj as any)[key]),
  );
}

export const VolatilityConesProvider = ({ children, providerKey }: Props) => {
  const { sendMessage, subscribeToChannel } = useContext(WebSocketContext);
  const [data, setData] = useState<VolConeData | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const STATE_PATH = [KEY, providerKey];

  const { token, setToken } = useContext(WidgetContext);

  const [lastTimestamp, setLastTimestamp] = usePersistentState(
    STATE_PATH,
    'lastTimestamp',
    new Date().toISOString(),
  );
  const [isLive, setIsLive] = usePersistentState(STATE_PATH, 'isLive', true);

  const [selectedPeriod, setSelectedPeriod] = usePersistentState(
    STATE_PATH,
    'selectedPeriod',
    WindowEnum.M1,
  );
  const [selectedTenor, setSelectedTenor] = usePersistentState(
    STATE_PATH,
    'selectedTenor',
    MetricEnum.ATMF,
  );

  const handleResponse = useCallback((response: any, timestamp: string) => {
    const result = response?.result;

    if (isVolConeResponse(result)) {
      const tenors = Object.keys(result.current || {});
      const items: VolConeItem[] = tenors.map((tenor) => ({
        tenor,
        current: result.current[tenor],
        low: result.low[tenor],
        p25: result.p25[tenor],
        median: result.median[tenor],
        p75: result.p75[tenor],
        high: result.high[tenor],
      }));

      setData(items);
      setLastTimestamp(timestamp);
    } else {
      setData(null);
    }
    setIsLoading(false);
  }, []);

  const fetchVolCone = useCallback(
    (params: VolConeRequestParams) => {
      setIsLoading(true);
      const id = `VOL_CONE_${providerKey}_${params.underlying}_${params.metric}_${params.window}`;
      const req = {
        jsonrpc: '2.0',
        id,
        method: 'vol_cone',
        params,
      };
      sendMessage(JSON.stringify(req));
      subscribeToChannel(id, (res: any) => handleResponse(res, params.as_at_time));
    },
    [sendMessage, subscribeToChannel, providerKey, handleResponse],
  );

  const resetToLive = useCallback(() => {
    setIsLive(true);
    const now = new Date().toISOString();
    setLastTimestamp(now);
    fetchVolCone({
      underlying: token,
      metric: selectedTenor,
      window: selectedPeriod,
      format: 'json',
      as_at_time: now,
    });
  }, [token, selectedTenor, selectedPeriod, fetchVolCone]);

  const setHistoricalDate = useCallback(
    (timestamp: string) => {
      setIsLive(false);
      setLastTimestamp(timestamp);
      fetchVolCone({
        underlying: token,
        metric: selectedTenor,
        window: selectedPeriod,
        format: 'json',
        as_at_time: timestamp,
      });
    },
    [token, selectedTenor, selectedPeriod, fetchVolCone],
  );

  const updateParamsLoad = useCallback(() => {
    const params = {
      underlying: token,
      metric: selectedTenor,
      window: selectedPeriod,
      format: 'json',
      as_at_time: lastTimestamp,
    };
    fetchVolCone(params);
  }, [fetchVolCone, selectedPeriod, selectedTenor, token, lastTimestamp]);

  useEffect(() => {
    updateParamsLoad();
  }, [fetchVolCone, selectedPeriod, selectedTenor, token, updateParamsLoad]);

  return (
    <VolatilityConesContext.Provider
      value={{
        data,
        isLoading,
        fetchVolCone,
        lastTimestamp,
        setLastTimestamp,
        token,
        setToken,
        selectedPeriod,
        setSelectedPeriod,
        selectedTenor,
        setSelectedTenor,
        isLive,
        resetToLive,
        setHistoricalDate,
      }}
    >
      {children}
    </VolatilityConesContext.Provider>
  );
};
